import { useState } from "react";
import { toast } from "sonner";
import { Download, Highlighter, Image as ImageIcon, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const FORMATS = [
  { id: "4:5", label: "IG / FB 1080×1350" },
  { id: "1:1", label: "Cuadrada 1080×1080" },
  { id: "9:16", label: "Historia 1080×1920" },
  { id: "16:9", label: "X / Threads 1200×675" },
  { id: "1.91:1", label: "Enlace FB 1200×630" },
];

export const SocialCards = ({ articleId, title, category, outlets, outletId, baseImage, onCard }) => {
  const [headline, setHeadline] = useState(title || "");
  const [kicker, setKicker] = useState(category || "");
  const [outlet, setOutlet] = useState(outletId || (outlets || [])[0]?.id || "");
  const [cards, setCards] = useState({});
  const [busy, setBusy] = useState("");
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(-0.33);
  const [zoom, setZoom] = useState(1);
  const [highlight, setHighlight] = useState("#FACC15");
  const [textScale, setTextScale] = useState(1);

  const templates = (outlets || []).find((o) => o.id === outlet)?.card_templates || [];

  const markSelection = () => {
    const input = document.querySelector('[data-testid="card-headline-input"]');
    if (!input) return;
    const { selectionStart: a, selectionEnd: b, value } = input;
    if (a === b) {
      toast.info("Seleccione en el titular la palabra que quiere resaltar");
      return;
    }
    setHeadline(`${value.slice(0, a)}**${value.slice(a, b)}**${value.slice(b)}`);
  };

  const generate = async (key, body) => {
    setBusy(key);
    try {
      const { data } = await api.post(`/articles/${articleId}/social-card`, {
        headline, kicker, outlet_id: outlet || null, image_url: baseImage || null,
        offset_x: offsetX, offset_y: offsetY, zoom, highlight_color: highlight,
        text_scale: textScale, ...body,
      });
      const url = `${data.url}?v=${Date.now()}`;
      setCards((c) => ({ ...c, [key]: url }));
      if (onCard) onCard(data.url);
      toast.success("Tarjeta generada");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border-t border-border mt-8 pt-6" data-testid="social-cards">
      <div className="kicker mb-1 flex items-center gap-2">
        <ImageIcon className="h-3.5 w-3.5" /> Plantillas para redes
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Con las plantillas del medio, la foto se coloca en la zona libre y el titular se monta en su
        sitio. Sin plantilla se genera una tarjeta con el logo y el color del medio.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="text-xs uppercase tracking-wider">Titular de la tarjeta</Label>
          <Input value={headline} onChange={(e) => setHeadline(e.target.value)} className="mt-1.5"
                 data-testid="card-headline-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Antetítulo</Label>
            <Input value={kicker} onChange={(e) => setKicker(e.target.value)} className="mt-1.5"
                   data-testid="card-kicker-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Medio</Label>
            <select
              value={outlet}
              onChange={(e) => setOutlet(e.target.value)}
              className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
              data-testid="card-outlet-select"
            >
              {(outlets || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="border border-border bg-muted/30 p-4 mb-4" data-testid="card-adjust-panel">
        <div className="kicker mb-3">Ajuste de la foto y resaltado</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Horizontal</Label>
            <input type="range" min="-1" max="1" step="0.05" value={offsetX}
                   onChange={(e) => setOffsetX(Number(e.target.value))}
                   className="w-full mt-2 accent-primary" data-testid="card-offset-x" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Vertical</Label>
            <input type="range" min="-1" max="1" step="0.05" value={offsetY}
                   onChange={(e) => setOffsetY(Number(e.target.value))}
                   className="w-full mt-2 accent-primary" data-testid="card-offset-y" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Acercar ({zoom.toFixed(2)}×)</Label>
            <input type="range" min="1" max="3" step="0.05" value={zoom}
                   onChange={(e) => setZoom(Number(e.target.value))}
                   className="w-full mt-2 accent-primary" data-testid="card-zoom" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-wider">Titular</Label>
            <div className="flex border border-input">
              {[["Pequeño", 0.85], ["Normal", 1], ["Grande", 1.2]].map(([label, value]) => (
                <button
                  key={label}
                  onClick={() => setTextScale(value)}
                  className={`text-xs px-2.5 py-1.5 transition-colors ${
                    textScale === value ? "bg-primary text-primary-foreground"
                                        : "text-muted-foreground hover:bg-muted/60"}`}
                  data-testid={`card-text-scale-${label.toLowerCase()}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={markSelection} data-testid="card-highlight-btn">
            <Highlighter className="h-3.5 w-3.5 mr-1.5" />Resaltar lo seleccionado
          </Button>
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-wider">Color del resaltado</Label>
            <input type="color" value={highlight} onChange={(e) => setHighlight(e.target.value)}
                   className="h-8 w-12 border border-input bg-background rounded"
                   data-testid="card-highlight-color" />
          </div>
          <Button size="sm" variant="ghost"
                  onClick={() => { setOffsetX(0); setOffsetY(-0.33); setZoom(1); }}
                  data-testid="card-reset-btn">Restablecer</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Las palabras entre <span className="mono">**dobles asteriscos**</span> salen en el color
          del resaltado. Mueva los controles y vuelva a generar la tarjeta para ver el cambio.
          Si la nota tiene video, la tarjeta lleva el sello «VIDEO» automáticamente.
        </p>
      </div>

      {templates.length > 0 && (
        <div className="mb-4">
          <div className="kicker mb-2">Plantillas del medio</div>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <Button key={t.id} size="sm" onClick={() => generate(t.id, { template_id: t.id })}
                      disabled={busy === t.id} data-testid={`gen-template-${t.id}`}>
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                {busy === t.id ? "Generando…" : t.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="kicker mb-2">Sin plantilla · por formato</div>
      <div className="flex flex-wrap gap-2">
        {FORMATS.map((f) => (
          <Button key={f.id} size="sm" variant="outline"
                  onClick={() => generate(f.id, { format: f.id })}
                  disabled={busy === f.id || !headline}
                  data-testid={`gen-card-${f.id.replace(/[:.]/g, "-")}`}>
            <Wand2 className="h-3.5 w-3.5 mr-1.5" />
            {busy === f.id ? "Generando…" : f.label}
          </Button>
        ))}
      </div>

      {Object.keys(cards).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5" data-testid="social-cards-results">
          {Object.entries(cards).map(([key, url]) => (
            <div key={key} className="border border-border p-2">
              <img src={url} alt={`Tarjeta ${key}`} className="w-full object-contain bg-muted" />
              <div className="flex items-center justify-between mt-2 gap-2">
                <span className="text-xs mono text-muted-foreground truncate">{key}</span>
                <a href={url} download className="text-xs flex items-center gap-1 hover:text-primary shrink-0"
                   data-testid={`download-card-${key.replace(/[:.]/g, "-")}`}>
                  <Download className="h-3.5 w-3.5" /> Descargar
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
